import { useState, useEffect } from "react";
import { toast } from "sonner";
import { articlesAPI, foldersAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);





/* ── One-click summarise control, shared by both card layouts ── */
const SummariseButton = ({ article, summarised, summarising, onSummarise, onOpen }) => (
  <button
    data-testid={`summarise-btn-${article.id}`}
    onClick={(e) => { e.stopPropagation(); summarised ? onOpen(article) : onSummarise(article); }}
    disabled={summarising}
    style={{
      display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
      fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em",
      color: summarised ? "#22C55E" : ACCENT,
      background: summarised ? "#22C55E14" : `${ACCENT}14`,
      border: `1px solid ${summarised ? "#22C55E33" : ACCENT + "33"}`,
      borderRadius: 5, padding: "4px 9px", cursor: summarising ? "wait" : "pointer",
    }}
  >
    {summarising && <span style={{ width: 9, height: 9, border: `2px solid ${ACCENT}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
    {summarising ? "SUMMARISING…" : summarised ? "SUMMARISED" : "SUMMARISE"}
  </button>
);

/* ── Article detail panel ── */
const ArticlePanel = ({ article, onClose, aiSummary, summarising, onSummarise }) => (
  <div
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", backdropFilter: "blur(4px)" }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{ width: 560, height: "100vh", background: SURFACE, borderLeft: `1px solid ${BORDER}`, overflowY: "auto", animation: "slideIn 0.25s ease" }}
    >
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 7, marginBottom: 10, flexWrap: "wrap" }}>
            <Badge label={article.relevance >= 85 ? "HIGH RELEVANCE" : "MEDIUM"} color={article.relevance >= 85 ? "#22C55E" : "#F59E0B"} />
            {article.paywall && <Badge label="PAYWALL" color={TEXT_DIM} />}
            <Badge label={article.company} color={ACCENT} />
          </div>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.4, fontFamily: "'DM Sans', sans-serif", marginBottom: 8 }}>{article.title}</h2>
          <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{article.publisher} · {article.published}</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_MUTED, padding: 4, flexShrink: 0 }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div style={{ padding: "20px 24px" }}>
        {/* AI summary */}
        <div style={{ background: `${ACCENT}0A`, border: `1px solid ${ACCENT}22`, borderRadius: 9, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: ACCENT, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>AI SUMMARY</div>
            {!aiSummary && (
              <button
                data-testid="panel-summarise-btn"
                onClick={() => onSummarise && onSummarise(article)}
                disabled={summarising}
                style={{
                  marginLeft: "auto", display: "flex", alignItems: "center", gap: 7,
                  background: summarising ? `${ACCENT}22` : ACCENT, color: "#fff", border: "none",
                  borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600,
                  cursor: summarising ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {summarising && <span style={{ width: 11, height: 11, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
                {summarising ? "Summarising…" : "Summarise"}
              </button>
            )}
          </div>
          {aiSummary ? (
            <p data-testid="panel-ai-summary" style={{ fontSize: 13, color: "#93C5FD", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiSummary}</p>
          ) : (
            <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6 }}>
              {summarising
                ? "Reading the article and writing an ICP-contextualised summary…"
                : "No summary yet. Summarise it to get key points, why it matters for this company, and the signal to use on the call."}
            </p>
          )}
        </div>

        {/* Snippet */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>EXCERPT</div>
          <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7, fontStyle: "italic", borderLeft: `2px solid ${BORDER}`, paddingLeft: 12 }}>{article.snippet}</p>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>TAGS</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {article.tags.map(t => (
              <span key={t} style={{ fontSize: 11, color: TEXT_MUTED, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 10px" }}>{t}</span>
            ))}
          </div>
        </div>

        {/* Meta */}
        {[
          { label: "Saved By", value: article.savedBy },
          { label: "Saved", value: article.savedAt },
          { label: "Company", value: article.company },
          { label: "Folder", value: article.folder || "—" },
        ].map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", minWidth: 80, letterSpacing: "0.06em" }}>{r.label.toUpperCase()}</span>
            <span style={{ fontSize: 12, color: TEXT_PRIMARY }}>{r.value}</span>
          </div>
        ))}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
          <button style={{ flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px", fontSize: 12, color: TEXT_MUTED, cursor: "pointer" }}>
            Move to Folder
          </button>
          <button style={{ flex: 1, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 8, padding: "10px", fontSize: 12, color: ACCENT, cursor: "pointer", fontWeight: 600 }}>
            View Source →
          </button>
        </div>
        <button style={{ width: "100%", marginTop: 8, background: "#200A0A", border: "1px solid #7F1D1D", borderRadius: 8, padding: "9px", fontSize: 12, color: "#EF4444", cursor: "pointer" }}>
          Remove from Saved
        </button>
      </div>
    </div>
  </div>
);

/* ── ROOT ── */
export default function InferenceSavedArticles() {
  const [activeFolder, setActiveFolder] = useState("all");
  const [filterCompany, setFilterCompany] = useState("All");
  const [filterRelevance, setFilterRelevance] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [articles, setArticles] = useState([]);
  const [folders, setFolders] = useState([{ id: "all", label: "All Saved", count: 0 }]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [summaryByUrl, setSummaryByUrl] = useState({});
  const [summarisingUrl, setSummarisingUrl] = useState(null);

  useEffect(() => {
    articlesAPI.summaries()
      .then(res => {
        const map = {};
        for (const s of res.data || []) {
          if (s.article_url) map[s.article_url] = s.summary || "";
        }
        setSummaryByUrl(map);
      })
      .catch(err => console.error("Failed to fetch article summaries:", err));
  }, []);

  // One-click AI summary. Cached server-side per article+company, so a second
  // click on an already-summarised article costs nothing and returns instantly.
  const handleSummarise = async (article) => {
    if (!article || summarisingUrl) return;
    if (summaryByUrl[article.url]) return;
    setSummarisingUrl(article.url);
    try {
      const res = await articlesAPI.summarize({
        article_url: article.url,
        article_title: article.title,
        company_name: article.company,
      });
      const text = res?.data?.summary || "";
      setSummaryByUrl(prev => ({ ...prev, [article.url]: text }));
      toast.success(`Summarised "${article.title.slice(0, 40)}${article.title.length > 40 ? "…" : ""}"`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not summarise this article. Please try again.");
    } finally {
      setSummarisingUrl(null);
    }
  };

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await articlesAPI.list();
        const mapped = (res.data || []).map(a => ({
          id: a.article_id || a.id,
          title: a.title || a.headline || "Untitled",
          publisher: a.publisher || (a.source_url ? new URL(a.source_url).hostname.replace("www.", "") : "Unknown"),
          company: a.company || a.company_name || "Unknown",
          published: a.published || a.date || a.saved_at || "",
          date: a.published || a.date || a.saved_at || "",
          folder: a.folder || "all",
          relevance: a.relevance || 70,
          paywall: a.paywall || false,
          snippet: a.snippet || "",
          summary: a.summary || a.snippet || "",
          url: a.source_url || a.url || "#",
          tags: a.tags || [],
          savedBy: a.saved_by || "You",
          savedAt: a.created_at ? timeAgo(a.created_at) : "N/A",
        }));
        setArticles(mapped);
        const folderNames = [...new Set(mapped.map(a => a.folder).filter(f => f !== "all"))];
        const newFolders = [
          { id: "all", label: "All Saved", count: mapped.length },
          ...folderNames.map(f => ({ id: f, label: f.charAt(0).toUpperCase() + f.slice(1), count: mapped.filter(a => a.folder === f).length })),
        ];
        setFolders(newFolders);
      } catch (err) {
        console.error("Failed to fetch articles:", err);
        toast.error("Could not load saved articles.");
      }
    };
    fetchArticles();
  }, []);

  const companies = ["All", ...Array.from(new Set(articles.map(a => a.company)))];

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await foldersAPI.create({ name: newFolderName.trim() });
      const newFolder = { id: newFolderName.trim().toLowerCase().replace(/\s+/g, "-"), label: newFolderName.trim(), count: 0 };
      setFolders(prev => [...prev, newFolder]);
      setNewFolderName("");
      setShowNewFolder(false);
    } catch (err) {
      console.error("Failed to create folder:", err);
      toast.error("Could not create folder.");
    }
  };

  const filtered = articles.filter(a => {
    if (activeFolder !== "all" && a.folder !== activeFolder) return false;
    if (filterCompany !== "All" && a.company !== filterCompany) return false;
    if (filterRelevance === "High" && a.relevance < 85) return false;
    if (filterRelevance === "Medium" && (a.relevance >= 85 || a.relevance < 60)) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase()) && !a.company.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        input::placeholder { color: #374151; }
        button { outline: none; }
      `}</style>

      <div style={{ display: "flex", flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%", padding: "0 28px" }}>

        {/* Left: folders */}
        <aside style={{ width: 200, paddingTop: 24, paddingRight: 24, flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 10 }}>FOLDERS</div>
          <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {folders.map(f => (
              <button key={f.id} onClick={() => setActiveFolder(f.id)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 10px", borderRadius: 7, border: "none",
                background: activeFolder === f.id ? `${ACCENT}18` : "transparent",
                color: activeFolder === f.id ? ACCENT : TEXT_MUTED,
                fontSize: 12, fontWeight: activeFolder === f.id ? 600 : 400,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                transition: "all 0.15s",
              }}
                onMouseEnter={e => { if (activeFolder !== f.id) e.currentTarget.style.background = "#161B27"; }}
                onMouseLeave={e => { if (activeFolder !== f.id) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                  {f.label}
                </div>
                <span style={{ fontSize: 10, color: activeFolder === f.id ? ACCENT : TEXT_DIM, fontFamily: "'DM Mono', monospace", background: activeFolder === f.id ? `${ACCENT}22` : BORDER + "88", borderRadius: 100, padding: "1px 6px" }}>{f.count}</span>
              </button>
            ))}
            <button data-testid="new-folder-btn" onClick={() => setShowNewFolder(true)} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 10px", borderRadius: 7, border: `1px dashed ${BORDER}`,
              background: "transparent", color: TEXT_DIM, fontSize: 12,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginTop: 8,
            }}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M12 4v16m8-8H4" /></svg>
              New Folder
            </button>
            {showNewFolder && (
              <div style={{ marginTop: 10, padding: 12, background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 8 }}>
                <input
                  data-testid="new-folder-input"
                  type="text"
                  placeholder="Folder name..."
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleCreateFolder()}
                  autoFocus
                  style={{
                    width: "100%", padding: "8px 10px", fontSize: 12,
                    background: BG, border: `1px solid ${BORDER}`, borderRadius: 6,
                    color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif",
                    outline: "none", marginBottom: 8,
                  }}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button data-testid="create-folder-btn" onClick={handleCreateFolder} style={{
                    flex: 1, padding: "6px 0", fontSize: 11, fontWeight: 600,
                    background: ACCENT, color: "#fff", border: "none", borderRadius: 5,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  }}>Create</button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} style={{
                    flex: 1, padding: "6px 0", fontSize: 11, fontWeight: 600,
                    background: "transparent", color: TEXT_DIM, border: `1px solid ${BORDER}`, borderRadius: 5,
                    cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  }}>Cancel</button>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* Main */}
        <main style={{ flex: 1, paddingTop: 24, paddingBottom: 40 }}>
          {/* Toolbar */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
            {/* Company filter */}
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {companies.map(c => (
                <button key={c} onClick={() => setFilterCompany(c)} style={{
                  fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
                  border: `1px solid ${filterCompany === c ? ACCENT : BORDER}`,
                  background: filterCompany === c ? `${ACCENT}18` : "transparent",
                  color: filterCompany === c ? ACCENT : TEXT_MUTED,
                  fontFamily: "'DM Sans', sans-serif",
                }}>{c}</button>
              ))}
            </div>
            <div style={{ width: 1, height: 18, background: BORDER }} />
            {/* Relevance filter */}
            <div style={{ display: "flex", gap: 5 }}>
              {["All", "High", "Medium"].map(r => (
                <button key={r} onClick={() => setFilterRelevance(r)} style={{
                  fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
                  border: `1px solid ${filterRelevance === r ? ACCENT : BORDER}`,
                  background: filterRelevance === r ? `${ACCENT}18` : "transparent",
                  color: filterRelevance === r ? ACCENT : TEXT_MUTED,
                  fontFamily: "'DM Sans', sans-serif",
                }}>{r === "All" ? "All Relevance" : `${r} Relevance`}</button>
              ))}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{filtered.length} articles</span>
              {/* View toggle */}
              {["list", "grid"].map(v => (
                <button key={v} onClick={() => setViewMode(v)} style={{
                  width: 28, height: 28, borderRadius: 6, border: `1px solid ${viewMode === v ? ACCENT : BORDER}`,
                  background: viewMode === v ? `${ACCENT}18` : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                }}>
                  {v === "list"
                    ? <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={viewMode === v ? ACCENT : TEXT_DIM} strokeWidth={2}><path d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                    : <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={viewMode === v ? ACCENT : TEXT_DIM} strokeWidth={2}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                  }
                </button>
              ))}
            </div>
          </div>

          {/* Articles */}
          {articles.length === 0 ? (
            <div data-testid="articles-empty-state" style={{ textAlign: "center", padding: "80px 0" }}>
              <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No saved articles yet.</p>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>Save articles from any analysis to build your reading list.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <p style={{ fontSize: 14, color: TEXT_MUTED }}>No articles match your filters.</p>
            </div>
          ) : viewMode === "list" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map(a => (
                <div key={a.id}
                  onClick={() => setSelected(a)}
                  style={{
                    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
                    padding: "14px 18px", cursor: "pointer", transition: "border-color 0.15s",
                    display: "flex", gap: 14, alignItems: "flex-start",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#2D3748"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
                >
                  {/* Relevance dot */}
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.relevance >= 85 ? "#22C55E" : "#F59E0B", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 7, marginBottom: 6, flexWrap: "wrap" }}>
                      <Badge label={a.company} color={ACCENT} />
                      {a.paywall && <Badge label="PAYWALL" color={TEXT_DIM} />}
                      {a.tags.slice(0, 2).map(t => (
                        <span key={t} style={{ fontSize: 10, color: TEXT_DIM, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 3, padding: "1px 6px", fontFamily: "'DM Mono', monospace" }}>{t}</span>
                      ))}
                    </div>
                    <h3 style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.4, marginBottom: 5, fontFamily: "'DM Sans', sans-serif" }}>{a.title}</h3>
                    <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 6 }}>{a.snippet}</p>
                    <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", display: "flex", gap: 10 }}>
                      <span>{a.publisher}</span><span>·</span><span>{a.published}</span><span>·</span><span>Saved {a.savedAt} by {a.savedBy}</span>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: a.relevance >= 85 ? "#22C55E" : "#F59E0B", fontFamily: "'DM Sans', sans-serif" }}>{a.relevance}</div>
                      <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>RELEVANCE</div>
                    </div>
                    <SummariseButton
                      article={a}
                      summarised={!!summaryByUrl[a.url]}
                      summarising={summarisingUrl === a.url}
                      onSummarise={handleSummarise}
                      onOpen={setSelected}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {filtered.map(a => (
                <div key={a.id}
                  onClick={() => setSelected(a)}
                  style={{
                    background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
                    padding: "16px 18px", cursor: "pointer", transition: "border-color 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#2D3748"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
                >
                  <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                    <Badge label={a.company} color={ACCENT} />
                    <Badge label={`${a.relevance}`} color={a.relevance >= 85 ? "#22C55E" : "#F59E0B"} />
                    {a.paywall && <Badge label="PAYWALL" color={TEXT_DIM} />}
                  </div>
                  <h3 style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.4, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>{a.title}</h3>
                  <p style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 10 }}>{a.snippet.slice(0, 120)}…</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{a.publisher} · {a.published}</div>
                    <div style={{ marginLeft: "auto" }}>
                      <SummariseButton
                        article={a}
                        summarised={!!summaryByUrl[a.url]}
                        summarising={summarisingUrl === a.url}
                        onSummarise={handleSummarise}
                        onOpen={setSelected}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {selected && (
        <ArticlePanel
          article={selected}
          onClose={() => setSelected(null)}
          aiSummary={summaryByUrl[selected.url]}
          summarising={summarisingUrl === selected.url}
          onSummarise={handleSummarise}
        />
      )}
    </div>
  );
}
